import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { ArtistApplication } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import './AdminArtistApplications.css';

const AdminArtistApplications: React.FC = () => {
  const [applications, setApplications] = useState<ArtistApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const { success, error: showError } = useToast();

  const fetchApplications = async (): Promise<void> => {
    try {
      const response = await api.get<ArtistApplication[]>('/admin/artist-applications');
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const updateStatus = async (id: number, status: string): Promise<void> => {
    setUpdatingId(id);
    try {
      await api.put(`/admin/artist-applications/${id}/status`, {
        status,
        adminNotes: notes[id] || null
      });
      fetchApplications();
      success(`Application ${status.toLowerCase()}.`);
    } catch (error) {
      showError('Failed to update status.');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading applications...</div>;
  }

  return (
    <div className="admin-artist-applications">
      <div className="container">
        <div className="page-header">
          <div className="page-header-left">
            <Link to="/admin" className="back-button">
              <i className="fas fa-arrow-left"></i> Back to Admin Panel
            </Link>
            <h1>Artist Applications</h1>
          </div>
        </div>

        <div className="application-grid">
          {applications.map((application) => (
            <div key={application.id} className="application-card">
              <h3>{application.displayName}</h3>
              <p><strong>Status:</strong> {application.status}</p>
              <p><strong>Target Price:</strong> {application.targetPricePoint || '—'}</p>
              <p><strong>Instagram:</strong> {application.instagram || '—'}</p>
              <p><strong>Website:</strong> {application.website || '—'}</p>
              <p className="statement"><strong>Statement:</strong> {application.statement}</p>

              {application.attachments.length > 0 && (
                <div className="attachments">
                  <strong>Attachments:</strong>
                  <ul>
                    {application.attachments.map((file) => (
                      <li key={file.id}>
                        <a href={file.fileUrl} target="_blank" rel="noreferrer">{file.fileName}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="form-group">
                <label>Admin Notes</label>
                <textarea
                  rows={3}
                  value={notes[application.id] || application.adminNotes || ''}
                  onChange={(e) => setNotes(prev => ({ ...prev, [application.id]: e.target.value }))}
                />
              </div>
              <div className="actions-row">
                <button
                  className="btn btn-primary"
                  onClick={() => updateStatus(application.id, 'Approved')}
                  disabled={updatingId === application.id}
                >
                  {updatingId === application.id && <span className="btn-spinner" aria-hidden="true"></span>}
                  Approve
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => updateStatus(application.id, 'Rejected')}
                  disabled={updatingId === application.id}
                >
                  {updatingId === application.id && <span className="btn-spinner" aria-hidden="true"></span>}
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminArtistApplications;
